/**
 * PATH: packages/job-hub/src/jobs/tabs/JobQaPackPanel.tsx
 * FILE_ID: JOBHUB-JOBS-QA-PACK-PANEL-001
 * PURPOSE: Pack/report generation for the active QA list, inside the job QA
 *          tab — pick a type (QA Pack / Inspection / NCR summary), queue
 *          generation, watch the Celery lifecycle, download the PDF.
 * WHY:     Wave 3 GAP fix (rank 10): pack generation only existed in the QA
 *          hub's Reports page; operators working a job had to leave the job
 *          to produce the handover pack. Reuses the SAME backend flow
 *          (POST /api/qa/reports/generate/ → generate_qa_pack_task).
 * LANDMINE: Generation is async (queued → generating → generated | failed).
 *          The list poll (5s) runs ONLY while a row is in-flight — don't
 *          make it unconditional or every open job tab polls forever.
 * RELATED:
 * - JOBHUB-JOBS-TAB-QA-001 (the frame that renders this)
 * - JOBHUB-QA-API-001 (listReports/generateReport/downloadReport)
 * - apps/qa/src/pages/reports/* (the QA-hub sibling surface)
 */
import { useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertCircle, Download, ExternalLink, FileText, Loader2, Package } from 'lucide-react'
import { buildHubDirectLink } from '@quotech/ui'
import { cn } from '@quotech/tenant-shared/lib/utils'
import type { QaApi, QaReport } from '../qa/qaApi'
import { openQaReportDownload } from '../qa/downloadBlobFile'
import { QA_PACK_ACTIONS_CLASS, QA_PACK_TYPE_ROW_CLASS } from './job-qa/qaWorkspaceLayout'

const PACK_TYPES = [
  { value: 'qa_pack', label: 'QA Pack' },
  { value: 'inspection_report', label: 'Inspection report' },
  { value: 'ncr_pack', label: 'NCR summary' },
] as const

const IN_FLIGHT = new Set(['queued', 'generating'])

// S4 rank 14: a pack that stays queued/generating beyond this is treated as
// stuck (Celery worker busy/dead) — show guidance + re-queue instead of an
// infinite spinner. QaReport carries no created_at, so we clock from when
// THIS panel first observed the row in flight (conservative: never early).
export const QA_PACK_STUCK_MS = 2 * 60_000

function statusLabel(r: QaReport): { text: string; tone: string } {
  if (r.status === 'generated') return { text: 'Ready', tone: 'text-status-good' }
  if (r.status === 'failed') return { text: 'Failed', tone: 'text-destructive' }
  if (r.status === 'generating') return { text: 'Generating…', tone: 'text-status-warn' }
  return { text: 'Queued…', tone: 'text-muted-foreground' }
}

export function JobQaPackPanel({ inspectionId, api }: { inspectionId: string; api: QaApi }) {
  const qc = useQueryClient()
  const [packType, setPackType] = useState<string>('qa_pack')
  const [error, setError] = useState<string | null>(null)
  const [downloadingId, setDownloadingId] = useState<string | null>(null)

  const reportsQuery = useQuery({
    queryKey: ['qa-reports', inspectionId],
    queryFn: () => api.listReports(inspectionId),
    enabled: !!inspectionId,
    // Poll only while generation is in flight; otherwise stay quiet.
    refetchInterval: (query) =>
      (query.state.data ?? []).some((r) => IN_FLIGHT.has(r.status)) ? 5000 : false,
  })

  const generate = useMutation({
    // Optional override so a stuck row can re-queue ITS type, not the picker's.
    mutationFn: (typeOverride?: string) => api.generateReport(inspectionId, typeOverride ?? packType),
    onSuccess: () => {
      setError(null)
      qc.invalidateQueries({ queryKey: ['qa-reports', inspectionId] })
    },
    onError: () => setError('Failed to queue pack generation. Please try again.'),
  })

  const download = async (report: QaReport) => {
    setDownloadingId(report.id)
    setError(null)
    try {
      openQaReportDownload(api.reportDownloadUrl(report.id))
    } catch {
      setError('Download failed. Please try again.')
    } finally {
      setDownloadingId(null)
    }
  }

  const reports = reportsQuery.data ?? []

  // First-observed-in-flight clock per report id. The 5s in-flight poll
  // re-renders this component, so the stuck check re-evaluates without its
  // own timer. Rows that leave the in-flight set are forgotten.
  const inFlightSince = useRef(new Map<string, number>())
  const now = Date.now()
  for (const r of reports) {
    if (IN_FLIGHT.has(r.status)) {
      if (!inFlightSince.current.has(r.id)) inFlightSince.current.set(r.id, now)
    } else {
      inFlightSince.current.delete(r.id)
    }
  }
  const isStuck = (r: QaReport) =>
    IN_FLIGHT.has(r.status) && now - (inFlightSince.current.get(r.id) ?? now) >= QA_PACK_STUCK_MS

  return (
    <div className="min-w-0 overflow-x-hidden rounded-xl border border-border bg-card p-4" data-testid="job-qa-pack-panel">
      <div className="mb-2 flex min-w-0 flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Package className="h-4 w-4 text-muted-foreground" aria-hidden />
          <p className="text-sm font-semibold text-foreground">QA packs &amp; reports</p>
        </div>
        <a
          href={buildHubDirectLink('qa', '/reports')}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground hover:text-primary transition-colors"
          title="Open the full Reports page in the QA hub (drafts, sharing, editor)"
        >
          <ExternalLink className="h-3 w-3" aria-hidden /> QA hub reports
        </a>
      </div>

      <div className={QA_PACK_ACTIONS_CLASS}>
        <div role="radiogroup" aria-label="Pack type" className={QA_PACK_TYPE_ROW_CLASS}>
          {PACK_TYPES.map(({ value, label }) => (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={packType === value}
              onClick={() => setPackType(value)}
              className={cn(
                'px-2.5 py-1 rounded-md text-xs font-medium transition-colors',
                packType === value
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {label}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => generate.mutate(undefined)}
          disabled={generate.isPending}
          className="inline-flex w-fit items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors"
        >
          {generate.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
          ) : (
            <FileText className="h-3.5 w-3.5" aria-hidden />
          )}
          Generate
        </button>
      </div>

      {error && (
        <div className="mt-2 flex items-start gap-2 text-xs text-destructive">
          <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" aria-hidden />
          <span>{error}</span>
        </div>
      )}

      {reportsQuery.isLoading ? (
        <p className="mt-3 text-xs text-muted-foreground">Loading packs…</p>
      ) : reports.length > 0 ? (
        <ul className="mt-3 divide-y divide-border/60" data-testid="qa-pack-list">
          {reports.map((r) => {
            const stuck = isStuck(r)
            const s = stuck
              ? { text: 'Still queued — may be stuck', tone: 'text-status-warn' }
              : statusLabel(r)
            return (
              <li key={r.id} className="flex items-center gap-2 py-1.5">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs font-medium text-foreground" title={r.report_name}>
                    {r.report_name}
                  </p>
                  <p className="text-[11px] text-muted-foreground">
                    <span className={s.tone}>{s.text}</span>
                    {r.generated_at ? ` · ${new Date(r.generated_at).toLocaleDateString()}` : ''}
                  </p>
                  {stuck && (
                    <p className="text-[11px] text-muted-foreground" data-testid="qa-pack-stuck-hint">
                      Generation has been pending for over 2 minutes. The report worker may be
                      busy — re-queue below, or check the QA hub Reports page.
                    </p>
                  )}
                </div>
                {stuck ? (
                  <button
                    type="button"
                    onClick={() => generate.mutate(r.report_type)}
                    disabled={generate.isPending}
                    className="shrink-0 inline-flex items-center gap-1 rounded-md border border-status-warn-border px-2 py-1 text-[11px] font-medium text-status-warn hover:bg-status-warn-muted disabled:opacity-50 transition-colors"
                    title="Queue a fresh generation of this pack type"
                  >
                    Re-queue
                  </button>
                ) : IN_FLIGHT.has(r.status) && (
                  <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground shrink-0" aria-hidden />
                )}
                {r.status === 'generated' && (
                  <button
                    type="button"
                    onClick={() => void download(r)}
                    disabled={downloadingId === r.id}
                    className="shrink-0 inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-[11px] font-medium text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-50 transition-colors"
                    title={`Download ${r.report_name}.pdf`}
                  >
                    {downloadingId === r.id ? (
                      <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                    ) : (
                      <Download className="h-3 w-3" aria-hidden />
                    )}
                    PDF
                  </button>
                )}
              </li>
            )
          })}
        </ul>
      ) : (
        <p className="mt-3 text-xs text-muted-foreground">
          No packs yet — pick a type and Generate to build the handover pack for this QA list.
        </p>
      )}
    </div>
  )
}

export default JobQaPackPanel
